import i18n from "../../../i18n";import { useEffect, useMemo, useState } from "react";
import { CalendarDays } from "lucide-react";
import { AppLayout, PageBackLink } from "../../../components/layout";
import { ContentCard, EmptyState, LoadingState, PageHeader, ui } from "../../../components/ui";
import { cn } from "../../../lib/utils";
import { HolidayFilterBar } from "../components";
import {
  fetchHoliday,
  fetchAllEvents
} from "../../../services/holidayService";
import { fetchDepartments, fetchWorkLocations } from "../../../services/holidayService";

function formatDate(dateStr) {
  const d = new Date(dateStr);
  const months = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec"];

  return `${d.getDate()} ${months[d.getMonth()]} ${d.getFullYear()}`;
}

export function ESSHolidaysPage() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [activeSection, setActiveSection] = useState("Holidays");
  const [typeFilter, setTypeFilter] = useState("All types");
  const [visibilityFilter, setVisibilityFilter] = useState("All visibility");
  const [holidayTypes, setHolidayTypes] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [locations, setLocations] = useState([]);
  const [scopeFilter, setScopeFilter] = useState("");
  const [loadError, setLoadError] = useState(null);



  useEffect(() => {
    fetchHoliday()
    .then((data) => setHolidayTypes(Array.isArray(data) ? data : data?.data ?? []))
    .catch((error) => {
      setHolidayTypes([]);
      setLoadError(error.message || "Failed to load holidays types.");
    });
  }, []);

  useEffect(() => {
    const loadEvents = async () => {
      setLoading(true);

      try {
        const allEvents = await fetchAllEvents();

        const sectionEvents =
          activeSection === "Holidays" ?
          allEvents.filter((event) => event.type === "Public Holiday") :
          allEvents.filter((event) => event.type !== "Public Holiday");

        setEvents(sectionEvents);
      } catch (error) {
        setEvents([]);
        setLoadError(error.message || "Failed to load holidays and events.");
      } finally {
        setLoading(false);
      }
    };

    loadEvents();
  }, [activeSection]);

  useEffect(() => {
    if (visibilityFilter === "Department" && departments.length === 0) {
      fetchDepartments()
      .then((data) => setDepartments(Array.isArray(data) ? data : data?.data ?? []))
      .catch((error) => {
        setDepartments([]);
        setLoadError(error.message || "Failed to load departments.");
      });
    }
    if (visibilityFilter === "Work Location" && locations.length === 0) {
      fetchWorkLocations()
      .then((data) => setLocations(Array.isArray(data) ? data : data?.data ?? []))
      .catch((error) => {
        setLocations([]);
        setLoadError(error.message || "Failed to load departments.");
      });
    }
  }, [visibilityFilter, departments.length, locations.length]);



  const sectionTypeOptions = useMemo(
    () =>
      activeSection === "Holidays" ?
      holidayTypes.filter(
        (type) => type.categoryName === "Public Holiday"
      ) :
      holidayTypes.filter(
        (type) => type.categoryName !== "Public Holiday"
      ),
    [holidayTypes, activeSection]
  );

  const upcomingEvents = useMemo(() => {
    const d = new Date();
    const today = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

    const filtered = events.filter((e) => {
      if (e.start < today) return false;

      const matchSearch =
      e.title.toLowerCase().includes(search.toLowerCase()) ||
      (e.description || "").toLowerCase().includes(search.toLowerCase());

      const matchType = typeFilter === "All types" || e.type === typeFilter;

      const matchVisibility =
      visibilityFilter === "All visibility" || e.visibility === visibilityFilter;

      return matchSearch && matchType && matchVisibility;
    });

    return filtered.sort((a, b) => new Date(a.start) - new Date(b.start));
  }, [events, search, typeFilter, visibilityFilter]);


  return (
    <AppLayout>
        {loadError && (
              <div className="mb-4 rounded-xl border border-[var(--color-error)]/25 bg-[var(--color-error)]/10 px-4 py-3 text-sm text-[var(--color-error)]">
                {loadError}
              </div>
            )}
        <PageBackLink to="/" />
            <PageHeader
        title={i18n.t("dateTime.holidays")}
        />


            <div className="mt-4 border-b border-[var(--color-text-border)]">
        <div className="flex items-center gap-8 px-1">
          <button
            type="button"
            onClick={() => {
              setActiveSection("Holidays");
              setTypeFilter("All types");
            }}
            className={`relative pb-3 text-sm font-semibold transition-colors ${
            activeSection === "Holidays" ?
            "text-[var(--color-primary)]" :
            "text-[var(--color-text-secondary)] hover:text-foreground"}`
            }>

            Holidays

            {activeSection === "Holidays" &&
            <span className="absolute bottom-0 left-0 right-0 h-[2px] bg-[var(--color-primary)]" />
            }
          </button>

          <button
            type="button"
            onClick={() => {
              setActiveSection("Events");
              setTypeFilter("All types");
            }}
            className={`relative pb-3 text-sm font-semibold transition-colors ${
            activeSection === "Events" ?
            "text-[var(--color-primary)]" :
            "text-[var(--color-text-secondary)] hover:text-foreground"}`
            }>

            Events

            {activeSection === "Events" &&
            <span className="absolute bottom-0 left-0 right-0 h-[2px] bg-[var(--color-primary)]" />
            }
          </button>
        </div>
      </div>

            <HolidayFilterBar
        search={search}
        setSearch={setSearch}
        typeFilter={typeFilter}
        setTypeFilter={setTypeFilter}
        visibilityFilter={visibilityFilter}
        setVisibilityFilter={setVisibilityFilter}
        typeOptions={sectionTypeOptions}
        departments={departments}
        locations={locations}
        scopeFilter={scopeFilter}
        setScopeFilter={setScopeFilter} />





            <ContentCard title={i18n.t("holidays.upcomingEvents")}>
            {loading ?
        <LoadingState message="Loading upcoming events…" /> :
        upcomingEvents.length === 0 ?
        <EmptyState
          icon={CalendarDays}
          title={i18n.t("dateTime.noUpcomingHolidaysOrEvents")}
          message="No upcoming holidays or events for you." /> :


        <div className="flex flex-col gap-4 p-4 sm:p-6">
                {upcomingEvents.map((event) => {
            const isPublic = event.type === "Public Holiday";
            return (
              <div
                key={event.id}
                className={`${ui.card} px-5 sm:px-6 py-5 flex items-start gap-5`}>

                      <div
                  className={cn(
                    "h-12 w-12 rounded-xl grid place-items-center shrink-0",
                    isPublic ?
                    "bg-indigo-50 text-indigo-700" :
                    "bg-rose-50 text-rose-700"
                  )}>

                        <CalendarDays className="h-5 w-5" />
                      </div>
                      <div>
                        <div className="flex flex-wrap items-center gap-3 mb-2">
                          <div className="text-lg font-semibold text-foreground">
                            {event.title}
                          </div>
                          <div
                      className={cn(
                        "inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-semibold",
                        isPublic ?
                        "bg-indigo-50 text-indigo-700" :
                        "bg-rose-50 text-rose-700"
                      )}>

                            <span
                        className={cn(
                          "h-1.5 w-1.5 rounded-full",
                          isPublic ? "bg-indigo-500" : "bg-rose-500"
                        )} />

                            {event.type}
                          </div>
                        </div>
                       <div className="text-sm text-[var(--color-text-secondary)] font-medium">
                         <div>
                           {formatDate(event.start)}
                         </div>

                         <div className="text-xs text-[var(--color-text-secondary)] mt-1">
                           {event.startDay === event.endDay
                             ? event.startDay
                             : `${event.startDay} - ${event.endDay}`}
                         </div>

                         {!event.allDay && (
                           <div className="text-xs text-[var(--color-text-secondary)] mt-1">
                             {event.startTime} - {event.endTime}
                           </div>
                         )}
                       </div>
                      </div>
                    </div>);

          })}
              </div>
        }
            </ContentCard>

    </AppLayout>);

}